import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Plus, Search, Edit, AlertCircle, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { usePermissions } from '../context/PermissionsContext';
import { Alert, AlertDescription } from '../components/ui/alert';
import { logger } from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUSES = ['pending', 'due', 'paid', 'overdue', 'partial'];

const statusColors = {
  pending: 'bg-slate-100 text-slate-600',
  due: 'bg-amber-100 text-amber-800',
  paid: 'bg-green-100 text-green-800',
  overdue: 'bg-red-100 text-red-800',
  partial: 'bg-blue-100 text-blue-800'
};

export default function InstallmentsPage() {
  const [installments, setInstallments] = useState([]);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const { hasPermission, role } = usePermissions();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedInstallment, setSelectedInstallment] = useState(null);
  const [formData, setFormData] = useState({
    deal_id: '',
    installment_number: '',
    amount: '',
    due_date: '',
    status: 'pending',
    paid_amount: '0'
  });

  // Permission checks
  const canCreateInstallments = hasPermission('installments_create');
  const canUpdateInstallments = hasPermission('installments_update');
  const isAccountExecutive = role === 'account_executive';

  const fetchInstallments = useCallback(async () => {
    try {
      const url = statusFilter === 'all'
        ? `${API_URL}/api/installments`
        : `${API_URL}/api/installments?status=${statusFilter}`;
      
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setInstallments(data);
      }
    } catch (error) {
      logger.error('Error fetching installments:', error);
      toast.error('Failed to load installments');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const fetchDeals = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/deals`, { credentials: 'include' });
      if (response.ok) setDeals(await response.json());
    } catch (error) {
      logger.error('Error fetching deals:', error);
    }
  }, []);

  useEffect(() => {
    fetchInstallments();
    fetchDeals();
  }, [fetchInstallments, fetchDeals]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreateInstallment = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        deal_id: formData.deal_id,
        installment_number: parseInt(formData.installment_number),
        amount: parseFloat(formData.amount),
        due_date: new Date(formData.due_date).toISOString(),
        status: formData.status,
        paid_amount: parseFloat(formData.paid_amount) || 0
      };

      const response = await fetch(`${API_URL}/api/installments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Installment created successfully');
        setIsCreateOpen(false);
        resetForm();
        fetchInstallments();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create installment');
      }
    } catch (error) {
      logger.error('Error creating installment:', error);
      toast.error('Failed to create installment');
    }
  };

  const handleUpdateInstallment = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        amount: parseFloat(formData.amount),
        due_date: new Date(formData.due_date).toISOString(),
        status: formData.status,
        paid_amount: parseFloat(formData.paid_amount) || 0
      };

      const response = await fetch(`${API_URL}/api/installments/${selectedInstallment.installment_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Installment updated successfully');
        setIsEditOpen(false);
        resetForm();
        fetchInstallments();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update installment');
      }
    } catch (error) {
      logger.error('Error updating installment:', error);
      toast.error('Failed to update installment');
    }
  };

  const openEditDialog = (installment) => {
    setSelectedInstallment(installment);
    setFormData({
      deal_id: installment.deal_id,
      installment_number: installment.installment_number.toString(),
      amount: installment.amount.toString(),
      due_date: format(new Date(installment.due_date), 'yyyy-MM-dd'),
      status: installment.status,
      paid_amount: installment.paid_amount?.toString() || '0'
    });
    setIsEditOpen(true);
  };

  const resetForm = () => {
    setFormData({
      deal_id: '',
      installment_number: '',
      amount: '',
      due_date: '',
      status: 'pending',
      paid_amount: '0'
    });
    setSelectedInstallment(null);
  };

  const getDealTitle = (dealId) => {
    const deal = deals.find(d => d.deal_id === dealId);
    return deal?.title || 'Unknown Deal';
  };

  const filteredInstallments = installments.filter(inst => {
    const dealTitle = getDealTitle(inst.deal_id).toLowerCase();
    return dealTitle.includes(searchQuery.toLowerCase());
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold"></div>
      </div>
    );
  }

  return (
    <div className="space-y-3 animate-fade-in" data-testid="installments-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900">Installments</h1>
          <p className="text-slate-500 font-body text-sm mt-1">
            {isAccountExecutive ? 'View upcoming installments for your deals' : 'Track payment schedules for deals'}
          </p>
        </div>

        {canCreateInstallments && (
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button className="btn-accent h-7 px-3 text-xs" data-testid="create-installment-btn">
                <Plus size={14} className="mr-1" />
                Add Installment
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">Create Installment</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateInstallment} className="space-y-4">
                <div>
                  <Label htmlFor="deal_id">Deal *</Label>
                  <Select value={formData.deal_id} onValueChange={(v) => setFormData({ ...formData, deal_id: v })} required>
                    <SelectTrigger data-testid="installment-deal">
                      <SelectValue placeholder="Select deal" />
                    </SelectTrigger>
                    <SelectContent>
                      {deals.map(deal => (
                        <SelectItem key={deal.deal_id} value={deal.deal_id}>{deal.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="installment_number">Installment # *</Label>
                    <Input
                      id="installment_number"
                      type="number"
                      min="1"
                      value={formData.installment_number}
                      onChange={(e) => setFormData({ ...formData, installment_number: e.target.value })}
                      required
                      className="input-goldsands"
                      data-testid="installment-number"
                    />
                  </div>
                  <div>
                    <Label htmlFor="amount">Amount ($) *</Label>
                    <Input
                      id="amount"
                      type="number"
                      step="0.01"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      required
                      className="input-goldsands"
                      data-testid="installment-amount"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="due_date">Due Date *</Label>
                    <Input
                      id="due_date"
                      type="date"
                      value={formData.due_date}
                      onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                      required
                      className="input-goldsands"
                      data-testid="installment-due-date"
                    />
                  </div>
                  <div>
                    <Label htmlFor="status">Status</Label>
                    <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                      <SelectTrigger data-testid="installment-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUSES.map(s => (
                          <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => { setIsCreateOpen(false); resetForm(); }}>
                    Cancel
                  </Button>
                  <Button type="submit" className="btn-accent" data-testid="installment-submit">
                    Create Installment
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Account Executive notice */}
      {isAccountExecutive && (
        <Alert className="bg-blue-50 border-blue-200 py-2">
          <Eye className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-800 text-xs">
            You are viewing installments for your assigned deals. This is a read-only view. Contact Finance for any changes.
          </AlertDescription>
        </Alert>
      )}

      {/* Filters - Compact utility bar */}
      <div className="flex flex-col md:flex-row gap-2 items-stretch md:items-center bg-white border rounded-lg px-2 py-1.5 shadow-sm">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={14} />
          <Input
            placeholder="Search by deal..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-7 h-7 text-xs border-slate-200"
            data-testid="search-installments"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full md:w-36 h-7 text-xs" data-testid="filter-status">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {STATUSES.map(s => (
              <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Installments Table */}
      <Card className="card-goldsands">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Deal</TableHead>
                <TableHead>Installment #</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Paid</TableHead>
                <TableHead>Due Date</TableHead>
                <TableHead>Status</TableHead>
                {canUpdateInstallments && <TableHead className="w-12"></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredInstallments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canUpdateInstallments ? 7 : 6} className="text-center py-8 text-slate-500">
                    {isAccountExecutive ? 'No installments found for your assigned deals' : 'No installments found'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredInstallments.map((inst) => (
                  <TableRow key={inst.installment_id} className="table-row-hover">
                    <TableCell className="font-medium">{getDealTitle(inst.deal_id)}</TableCell>
                    <TableCell>#{inst.installment_number}</TableCell>
                    <TableCell>${inst.amount?.toLocaleString()}</TableCell>
                    <TableCell>${inst.paid_amount?.toLocaleString() || 0}</TableCell>
                    <TableCell>{format(new Date(inst.due_date), 'MMM d, yyyy')}</TableCell>
                    <TableCell>
                      <Badge className={`${statusColors[inst.status]} capitalize`}>
                        {inst.status}
                      </Badge>
                    </TableCell>
                    {canUpdateInstallments && (
                      <TableCell>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => openEditDialog(inst)}
                          data-testid={`edit-installment-${inst.installment_id}`}
                        >
                          <Edit size={16} />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Edit Installment</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateInstallment} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit_amount">Amount ($) *</Label>
                <Input
                  id="edit_amount"
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  required
                  className="input-goldsands"
                />
              </div>
              <div>
                <Label htmlFor="edit_paid_amount">Paid Amount ($)</Label>
                <Input
                  id="edit_paid_amount"
                  type="number"
                  step="0.01"
                  value={formData.paid_amount}
                  onChange={(e) => setFormData({ ...formData, paid_amount: e.target.value })}
                  className="input-goldsands"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit_due_date">Due Date *</Label>
                <Input
                  id="edit_due_date"
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  required
                  className="input-goldsands"
                />
              </div>
              <div>
                <Label htmlFor="edit_status">Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.map(s => (
                      <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => { setIsEditOpen(false); resetForm(); }}>
                Cancel
              </Button>
              <Button type="submit" className="btn-accent">
                Update Installment
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
